import React, { useState } from 'react';
import {
  Table, Button, Modal, Form, FormItem, Input, Select, Card, Upload,
  useToast, showConfirm, NumberInput, TextArea
} from '../ui';
import type { Column } from '../ui';
import { PlusIcon, PencilIcon, TrashIcon, PhotoIcon, MagnifyingGlassIcon, CloudArrowUpIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import type { Dress, Brand, Category, Branch } from '../config/supabase';
import { supabase } from '../config/supabase';
import { useDresses, useCreateDress, useUpdateDress, useDeleteDress, useDressColors } from '../hooks/api/useDresses';
import { useBrandsAll } from '../hooks/api/useBrands';
import { useBranchesAll } from '../hooks/api/useBranches';
import { useCategoriesAll } from '../hooks/api/useCategories';
import { generateEAN13, generateBarcodeImage, isValidEAN13 } from '../utils/barcode';
import CsvImportEdgeFunction from './CsvImportEdgeFunction';

const DressManagement: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'dresses' | 'import'>('dresses');
  
  // Modal and form state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDress, setEditingDress] = useState<Dress | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string>('');
  const [generatedBarcode, setGeneratedBarcode] = useState<string>('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadingDress, setUploadingDress] = useState<Dress | null>(null);
  
  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter state
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [colorFilter, setColorFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  
  const { success, error: showError } = useToast();

  // Modern 2025 API calls with automatic caching and deduplication
  const { 
    data: dressesData, 
    isLoading: dressesLoading, 
    error: dressesError 
  } = useDresses(
    currentPage,
    pageSize,
    searchTerm,
    brandFilter === 'all' ? undefined : brandFilter,
    categoryFilter === 'all' ? undefined : categoryFilter,
    colorFilter === 'all' ? undefined : colorFilter,
    branchFilter === 'all' ? undefined : branchFilter
  );

  const { 
    data: brands = [], 
    isLoading: brandsLoading 
  } = useBrandsAll();

  const {
    data: categories = [],
    isLoading: categoriesLoading
  } = useCategoriesAll();

  const {
    data: branchesData = [],
    isLoading: branchesLoading
  } = useBranchesAll();

  const { 
    data: colors = [], 
    isLoading: colorsLoading 
  } = useDressColors();

  // Mutations with automatic cache updates
  const createDressMutation = useCreateDress();
  const updateDressMutation = useUpdateDress();
  const deleteDressMutation = useDeleteDress();

  // Extract data from hooks
  const dresses = dressesData?.dresses || [];
  const totalCount = dressesData?.totalCount || 0;
  const loading = dressesLoading || brandsLoading || categoriesLoading || colorsLoading || branchesLoading;

  // Handle errors with useEffect to prevent infinite loops
  React.useEffect(() => {
    if (dressesError) {
      showError('Failed to fetch dresses');
    }
  }, [dressesError, showError]);

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    // No manual API call needed - TanStack Query handles this automatically!
  };

  const handleFilterChange = (filterType: 'brand' | 'category' | 'color' | 'branch', value: string) => {
    // Reset to first page when filters change
    setCurrentPage(1);

    switch (filterType) {
      case 'brand':
        setBrandFilter(value);
        break;
      case 'category':
        setCategoryFilter(value);
        break;
      case 'color':
        setColorFilter(value);
        break;
      case 'branch':
        setBranchFilter(value);
        break;
    }
  };

  const clearAllFilters = () => {
    setBrandFilter('all');
    setCategoryFilter('all');
    setColorFilter('all');
    setBranchFilter('all');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleAdd = () => {
    setEditingDress(null);
    setUploadedPhotoUrl('');
    setGeneratedBarcode(generateEAN13());
    setModalVisible(true);
  };

  const handleEdit = (dress: Dress) => {
    setEditingDress(dress);
    setUploadedPhotoUrl(dress.photo_url || '');
    setGeneratedBarcode(''); // Don't generate new barcode for editing
    setModalVisible(true);
  };

  const handleDelete = async (dress: Dress) => {
    showConfirm({
      title: 'Delete Dress',
      content: `Are you sure you want to delete "${dress.name}"?`,
      onConfirm: async () => {
        try {
          await deleteDressMutation.mutateAsync(dress.id);
          success('Dress deleted successfully');
        } catch (error: any) {
          showError('Error deleting dress: ' + error.message);
        }
      }
    });
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingDress(null);
    setUploadedPhotoUrl('');
    setGeneratedBarcode('');
  };

  const handleQuickUpload = (dress: Dress) => {
    setUploadingDress(dress);
    setUploadedPhotoUrl(dress.photo_url || '');
    setUploadModalVisible(true);
  };

  const handleUploadModalClose = () => {
    setUploadModalVisible(false);
    setUploadingDress(null);
    setUploadedPhotoUrl('');
  };

  const handleQuickUploadSubmit = async () => {
    if (!uploadingDress || !uploadedPhotoUrl) return;

    try {
      await updateDressMutation.mutateAsync({ 
        id: uploadingDress.id, 
        photo_url: uploadedPhotoUrl 
      });
      success('Photo updated successfully');
      handleUploadModalClose();
    } catch (error: any) {
      showError('Error updating photo: ' + error.message);
    }
  };

  const handleDownloadBarcode = (dress: Dress) => {
    try {
      if (!dress.barcode) {
        showError('No barcode available for this dress');
        return;
      }

      // Validate barcode format
      if (!isValidEAN13(dress.barcode)) {
        showError(`Invalid barcode format: "${dress.barcode}". Please edit the dress to generate a valid EAN13 barcode.`);
        return;
      }

      // Generate barcode image
      const barcodeDataUrl = generateBarcodeImage(dress.barcode);
      
      if (!barcodeDataUrl) {
        showError('Failed to generate barcode image');
        return;
      }
      
      // Create download link
      const link = document.createElement('a');
      link.download = `barcode-${dress.name.replace(/[^a-zA-Z0-9]/g, '_')}-${dress.barcode}.png`;
      link.href = barcodeDataUrl;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      success('Barcode downloaded successfully');
    } catch (error: any) {
      console.error('Barcode generation error:', error);
      showError('Error generating barcode: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSubmit = async (values: Record<string, any>) => {
    const dressData = {
      name: values.name,
      brand_id: values.brand_id,
      category_id: values.category_id,
      branch_id: values.branch_id || undefined,
      color: values.color,
      size: values.size,
      barcode: editingDress ? values.barcode : generatedBarcode,
      description: values.description,
      photo_url: uploadedPhotoUrl || undefined,
      rental_price: values.rental_price || 0,
    };

    try {
      if (editingDress) {
        await updateDressMutation.mutateAsync({ id: editingDress.id, ...dressData });
        success('Dress updated successfully');
      } else {
        await createDressMutation.mutateAsync(dressData);
        success('Dress created successfully');
      }
      handleModalClose();
    } catch (error: any) {
      showError('Error saving dress: ' + error.message);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    setUploading(true);
    try {
      // Check if user is authenticated
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('Current session:', session?.user?.email, 'Session valid:', !!session);
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error('Session error: ' + sessionError.message);
      }
      
      if (!session) {
        throw new Error('User not authenticated. Please log in again.');
      }

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `dress-photos/${fileName}`;

      console.log('Attempting upload to:', filePath);

      // Upload file to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('dress-photos')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error details:', {
          message: uploadError.message,
          name: uploadError.name
        });
        throw uploadError;
      }

      console.log('Upload successful:', uploadData);

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('dress-photos')
        .getPublicUrl(filePath);

      setUploadedPhotoUrl(publicUrlData.publicUrl);
      success('Photo uploaded successfully');
    } catch (error: any) {
      console.error('Photo upload error:', error);
      
      // If it's an RLS error, show specific message
      if (error.message.includes('row-level security') || error.message.includes('RLS')) {
        showError('Storage permission error. Please contact admin to set up storage policies.');
      } else {
        showError('Error uploading photo: ' + error.message);
      }
    } finally {
      setUploading(false);
    }
  };

  const columns: Column<Dress>[] = [
    {
      key: 'photo',
      title: 'Photo',
      width: 80,
      render: (_, dress) => (
        <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded">
          {dress.photo_url ? (
            <img 
              src={dress.photo_url} 
              alt={dress.name}
              className="w-full h-full object-cover rounded"
            />
          ) : (
            <PhotoIcon className="h-6 w-6 text-gray-400" />
          )}
        </div>
      ),
    },
    {
      key: 'barcode',
      title: 'Barcode',
      dataIndex: 'barcode',
      width: 120,
      sorter: true,
    },
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      key: 'brand',
      title: 'Brand',
      render: (_, dress) => dress.brands?.name || '-',
    },
    {
      key: 'category',
      title: 'Category',
      render: (_, dress) => dress.categories?.name || '-',
    },
    {
      key: 'color',
      title: 'Color',
      dataIndex: 'color',
    },
    {
      key: 'size',
      title: 'Size',
      dataIndex: 'size',
    },
    {
      key: 'rental_price',
      title: 'Rental Price',
      render: (_, dress) => `$${dress.rental_price?.toFixed(2) || '0.00'}`,
      align: 'right' as const,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 200,
      render: (_, dress) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDownloadBarcode(dress)}
            className="text-purple-600 hover:text-purple-800"
            title="Download Barcode"
          >
            <QrCodeIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleQuickUpload(dress)}
            className="text-green-600 hover:text-green-800"
            title="Upload Photo"
          >
            <CloudArrowUpIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(dress)}
            className="text-blue-600 hover:text-blue-800"
            title="Edit Dress"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(dress)}
            className="text-red-600 hover:text-red-800"
            title="Delete Dress"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <PhotoIcon className="h-6 w-6 mr-2" />
          Dress Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage dress inventory, pricing, and CSV imports
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('dresses')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'dresses'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <PhotoIcon className="h-5 w-5 inline mr-2" />
            Dress Management
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'import'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CloudArrowUpIcon className="h-5 w-5 inline mr-2" />
            CSV Import
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'dresses' ? (
        <>
          {/* Filters */}
          <Card className="bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">Filter Dresses</h3>
              {(brandFilter !== 'all' || categoryFilter !== 'all' || colorFilter !== 'all' || branchFilter !== 'all') && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  {[brandFilter, categoryFilter, colorFilter, branchFilter].filter(f => f !== 'all').length} filter(s) active
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Branch
                </label>
                <Select
                  value={branchFilter === 'all' ? undefined : branchFilter}
                  onChange={(value) => handleFilterChange('branch', String(value) || 'all')}
                  options={branchesData.map((branch: Branch) => ({
                    value: branch.id,
                    label: branch.name
                  }))}
                  placeholder="Select branch..."
                  showSearch={true}
                  allowClear={true}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Brand
                </label>
                <Select
                  value={brandFilter === 'all' ? undefined : brandFilter}
                  onChange={(value) => handleFilterChange('brand', String(value) || 'all')}
                  options={brands.map((brand: Brand) => ({
                    value: brand.id,
                    label: brand.name
                  }))}
                  placeholder="Select brand..."
                  showSearch={true}
                  allowClear={true}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Category
                </label>
                <Select
                  value={categoryFilter === 'all' ? undefined : categoryFilter}
                  onChange={(value) => handleFilterChange('category', String(value) || 'all')}
                  options={categories.map((category: Category) => ({
                    value: category.id,
                    label: category.name
                  }))}
                  placeholder="Select category..."
                  showSearch={true}
                  allowClear={true}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Color
                </label>
                <Select
                  value={colorFilter === 'all' ? undefined : colorFilter}
                  onChange={(value) => handleFilterChange('color', String(value) || 'all')}
                  options={colors.map((color: string) => ({
                    value: color,
                    label: color
                  }))}
                  placeholder="Select color..."
                  showSearch={true}
                  allowClear={true}
                  className="w-full"
                />
              </div>

              <div>
                <Button
                  variant="outline"
                  onClick={clearAllFilters}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </Card>

          <div className="flex justify-between items-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleAdd}
              className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-semibold px-6 py-3"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Dress
            </Button>
            
            <div className="ml-auto">
              <Input
                placeholder="Search dresses by name, barcode, or color..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page when searching
                }}
                leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
                className="w-80"
              />
            </div>
          </div>

          <Card>
            <Table
              columns={columns}
              dataSource={dresses}
              loading={loading}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: handlePageChange,
                onShowSizeChange: handlePageChange
              }}
            />
          </Card>
        </>
      ) : (
        <CsvImportEdgeFunction />
      )}

              <Modal
          isOpen={modalVisible}
          onClose={handleModalClose}
          title={editingDress ? 'Edit Dress' : 'Add Dress'}
          size="lg"
        >
        <Form
          onSubmit={handleSubmit}
          initialValues={editingDress || { barcode: generatedBarcode }}
        >
          <div className="grid grid-cols-2 gap-4">
            <FormItem
              label="Barcode"
              name="barcode"
              required
            >
              <Input 
                placeholder={editingDress ? "Enter barcode" : "Auto-generated"} 
                readOnly={!editingDress}
                value={editingDress ? editingDress.barcode : generatedBarcode}
                className={!editingDress ? "bg-gray-50" : ""}
                required 
              />
            </FormItem>

            <FormItem
              label="Name"
              name="name"
              required
            >
              <Input placeholder="Enter dress name" required />
            </FormItem>

            <FormItem
              label="Brand"
              name="brand_id"
              required
            >
              <Select 
                placeholder="Select brand" 
                options={brands.map((brand: Brand) => ({
                  value: brand.id,
                  label: brand.name
                }))}
              />
            </FormItem>

            <FormItem
              label="Category"
              name="category_id"
              required
            >
              <Select
                placeholder="Select category"
                options={categories.map((category: Category) => ({
                  value: category.id,
                  label: category.name
                }))}
              />
            </FormItem>

            <FormItem
              label="Branch"
              name="branch_id"
            >
              <Select
                placeholder="Select branch"
                options={branchesData.map((branch: Branch) => ({
                  value: branch.id,
                  label: branch.name
                }))}
              />
            </FormItem>

            <FormItem
              label="Color"
              name="color"
              required
            >
              <Input placeholder="Enter color" required />
            </FormItem>

            <FormItem
              label="Size"
              name="size"
              required
            >
              <Input placeholder="Enter size" required />
            </FormItem>

            <FormItem
              label="Rental Price"
              name="rental_price"
              required
            >
              <NumberInput 
                placeholder="0.00" 
                precision={2}
                min={0}
                prefix="$"
                required
              />
            </FormItem>
          </div>

          <FormItem label="Description" name="description">
            <TextArea 
              placeholder="Enter dress description" 
              rows={3}
            />
          </FormItem>

          <FormItem label="Photo">
            <div className="space-y-4">
              {uploadedPhotoUrl && (
                <div className="w-32 h-32 border border-gray-300 rounded-lg overflow-hidden">
                  <img 
                    src={uploadedPhotoUrl} 
                    alt="Dress preview" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <Upload
                accept="image/*"
                maxCount={1}
                beforeUpload={async (file) => {
                  await handlePhotoUpload(file);
                  return false;
                }}
              >
                <Button loading={uploading}>
                  {uploading ? 'Uploading...' : 'Upload Photo'}
                </Button>
              </Upload>
            </div>
          </FormItem>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              loading={createDressMutation.isPending || updateDressMutation.isPending}
            >
              {editingDress ? 'Update Dress' : 'Add Dress'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Quick Upload Modal */}
      <Modal
        isOpen={uploadModalVisible}
        onClose={handleUploadModalClose}
        title={`Upload Photo - ${uploadingDress?.name}`}
        size="md"
      >
        <div className="space-y-4">
          {uploadedPhotoUrl && (
            <div className="w-full max-w-md mx-auto">
              <div className="aspect-square border border-gray-300 rounded-lg overflow-hidden">
                <img 
                  src={uploadedPhotoUrl} 
                  alt="Dress preview" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
          
          <Upload
            accept="image/*"
            maxCount={1}
            beforeUpload={async (file) => {
              await handlePhotoUpload(file);
              return false;
            }}
          >
            <Button loading={uploading} size="lg" className="w-full">
              {uploading ? 'Uploading...' : 'Select Photo'}
            </Button>
          </Upload>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={handleUploadModalClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleQuickUploadSubmit}
              disabled={!uploadedPhotoUrl || uploading}
              loading={updateDressMutation.isPending}
            >
              Save Photo
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DressManagement; 